import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";

import { Badge, Button, type Column, Paragraph, Table, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  type GetOrphanedOrganizationUsersOutput,
  useBasecampOrganizationUsersOrphanedRetrieve,
} from "@api/rest/types-and-hooks";

const REASON_TOOLTIPS: Record<"removed" | "unlinked", string> = {
  removed:
    "Removed from org: this person's Auth0 organization membership was revoked, but their Frontera provider record is intact. Re-adding restores their access.",
  unlinked:
    "Account unlinked: this Frontera provider record isn't connected to an Auth0 account in our system — that doesn't mean no Auth0 account exists for them, just that the two aren't linked. Re-adding looks one up by email and relinks it; if none is found, this case needs manual intervention.",
};

const ORPHANED_USER_COLUMNS: Column<GetOrphanedOrganizationUsersOutput>[] = [
  {
    title: "Name",
    dataIndex: "full_name",
    key: "full_name",
    render: (value) => (
      <Paragraph displayType="loud" size="small" colorType="primary">
        {(value as string | null) ?? "—"}
      </Paragraph>
    ),
  },
  {
    title: "Email address",
    dataIndex: "email",
    key: "email",
    render: (value) => <Paragraph size="small">{(value as string | null) ?? "—"}</Paragraph>,
  },
  {
    title: "Reason",
    dataIndex: "auth0_id",
    key: "reason",
    render: (value) => {
      const reason = value ? "removed" : "unlinked";
      return (
        <div className="flex items-center gap-1.5">
          <Badge
            className="whitespace-nowrap"
            appearance={reason === "removed" ? "warn" : "error"}
            text={reason === "removed" ? "Removed from org" : "Account unlinked"}
          />
          <Tooltip text={REASON_TOOLTIPS[reason]} placement="right" showInfoIcon />
        </div>
      );
    },
  },
];

const buildActionsColumn = (
  onRestore: (row: GetOrphanedOrganizationUsersOutput) => void,
): Column<GetOrphanedOrganizationUsersOutput> => ({
  title: "Actions",
  dataIndex: "provider_id",
  key: "actions",
  className: "w-1 whitespace-nowrap",
  render: (_value, record) => (
    <Button
      variant="primary"
      buttonStyle="ghost"
      size="xs"
      text={
        <span className="flex items-center gap-1">
          <ArrowUturnLeftIcon className="h-4 w-4" />
          Re-add
        </span>
      }
      onClick={(e) => {
        e?.stopPropagation();
        onRestore(record);
      }}
    />
  ),
});

interface OrphanedProvidersTabProps {
  orgId: string;
  onRestore: (row: GetOrphanedOrganizationUsersOutput) => void;
}

export const OrphanedProvidersTab: React.FC<OrphanedProvidersTabProps> = ({ orgId, onRestore }) => {
  const { data, isLoading } = useBasecampOrganizationUsersOrphanedRetrieve(
    { organization_id: orgId },
    { query: { enabled: !!orgId } },
  );

  const orphanedUsers: GetOrphanedOrganizationUsersOutput[] =
    data?.status === 200 ? (Array.isArray(data.data) ? data.data : [data.data]) : [];

  const columns = [...ORPHANED_USER_COLUMNS, buildActionsColumn(onRestore)];

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[640px] px-4 sm:px-8 lg:px-16">
        <Table<GetOrphanedOrganizationUsersOutput>
          data={orphanedUsers}
          columns={columns}
          loading={isLoading}
          showSearch
          showPagination
          defaultItemsPerPage={25}
          itemsPerPageOptions={[10, 25, 50]}
        />
      </div>
    </div>
  );
};
